<template>
  <PForm
    ref="pForm"
    :heading="`${productHeading} Product`"
    :primary-cta="primaryCta"
    :secondary-cta="secondaryCta"
    :ternary-cta="ternaryCta"
    @close="closeForm"
    @reset="onReset"
    @submit.prevent="submitForm"
  >
    <template v-if="formState.edit" #header-info>
      <span>
        <PBadge
          :label="useChangeCase(formData.status_cd ?? '', 'capitalCase').value"
          :type="formData.status_cd"
          class="q-mx-md"
        />
        <PTooltip v-if="formData.status_cd === 'rejected'">{{
          formData.reason ?? "No reason provided"
        }}</PTooltip>
      </span>
    </template>
    <div class="row q-col-gutter-lg">
      <div class="col-8">
        <PInput
          v-model.trim="formData.name"
          help="Fully qualified name"
          label="Product/Item Name"
          placeholder="e.g Amul Cow Ghee 1 lt * 12"
          required
          @update:model-value="
            (v) => {
              formData.name = upperFirstFunc(v);
            }
          "
        />
      </div>
      <div class="col-4">
        <PSelectInput
          v-model="formData.unit"
          help="Cumulative unit"
          input-debounce="300"
          label="Package Unit"
          option-label="name"
          option-value="name"
          placeholder="Select unit"
          required
          :options="unref(unitFilter.entities)"
          @filter="unitFilter.onFilter"
          @filter-abort="unitFilter.onFilterAbort"
        ></PSelectInput>
      </div>
      <div class="col-4">
        <PSelectInput
          v-model="formData.category_id"
          :emit-value="false"
          :map-options="false"
          :options="unref(categoryFilter.entities)"
          :options-loading="unref(categoryFilter.loading)"
          input-debounce="300"
          label="Category"
          option-label="name"
          option-value="_id"
          placeholder="Select category"
          virtual-scroll-slice-size="25"
          virtual-scroll-item-size="53"
          required
          @virtual-scroll="categoryFilter.onScroll"
          @filter="categoryFilter.onFilter"
          @filter-abort="categoryFilter.onFilterAbort"
          @update:model-value="initSubCategoryFilter"
        ></PSelectInput>
      </div>
      <div class="col-4">
        <PSelectInput
          v-model="formData.subcategory_id"
          :emit-value="false"
          :map-options="false"
          :options="unref(subCategoryFilter?.entities)"
          :options-loading="unref(subCategoryFilter?.loading)"
          input-debounce="300"
          label="Subcategory"
          option-label="name"
          option-value="_id"
          placeholder="Select subcategory"
          virtual-scroll-slice-size="25"
          virtual-scroll-item-size="53"
          @virtual-scroll="subCategoryFilter.onScroll"
          @filter="subCategoryFilter?.onFilter"
          @filter-abort="subCategoryFilter?.onFilterAbort"
          @update:model-value="updateMarginExpectation"
        ></PSelectInput>
      </div>
      <div class="col-4">
        <PSelectInput
          v-model="formData.brand_id"
          :emit-value="false"
          :map-options="false"
          :options="unref(brandFilter.entities)"
          :options-loading="unref(brandFilter.loading)"
          input-debounce="300"
          label="Brand"
          option-label="name"
          option-value="_id"
          placeholder="Select brand"
          virtual-scroll-slice-size="25"
          virtual-scroll-item-size="53"
          @virtual-scroll="brandFilter.onScroll"
          @filter="brandFilter.onFilter"
          @filter-abort="brandFilter.onFilterAbort"
        ></PSelectInput>
      </div>
      <div class="col-12">
        <q-separator />
      </div>
      <div class="col-6 pl-0">
        <q-item v-ripple class="text-neutral">
          <q-item-section>
            <q-item-label>GST Exempted?</q-item-label>
            <q-item-label caption
              >Is this item exempted from tax under GST?</q-item-label
            >
          </q-item-section>
          <q-item-section avatar>
            <q-toggle v-model="formData.gst_exempted" :val="true" />
          </q-item-section>
        </q-item>
      </div>
      <div class="col-6"></div>
      <div v-if="!formData.gst_exempted" class="col-6">
        <PSelectInput
          v-model="formData.gst_rate"
          class="prepend-bg"
          label="GST"
          option-label="label"
          option-value="value"
          placeholder="Select GST rate"
          required
          :options="unref(gstFilter.entities)"
          :options-loading="unref(gstFilter.loading)"
          input-debounce="300"
          virtual-scroll-slice-size="25"
          virtual-scroll-item-size="53"
          @virtual-scroll="gstFilter.onScroll"
          @filter="gstFilter.onFilter"
          @filter-abort="gstFilter.onFilterAbort"
        >
          <template #prepend>
            <span class="text-sm">%</span>
          </template>
        </PSelectInput>
      </div>
      <div v-if="!formData.gst_exempted" class="col-6">
        <PInput
          v-model.trim.number="computedCess"
          class="prepend-bg"
          label="Cess"
          min="0"
          max="40"
          placeholder="e.g. 1"
          type="number"
          :validations="{
            type: 'integer',
            gte: 0,
            lte: 40,
          }"
          @keydown.space.prevent
        >
          <template #prepend>
            <span class="text-sm">%</span>
          </template>
        </PInput>
      </div>
      <div class="col-6">
        <PInput
          v-model.trim="formData.hsn"
          label="HSN"
          placeholder="Enter HSN code"
          :validations="{
            type: 'integer',
            lengths: [6, 8],
            required: true,
          }"
          @keydown.space.prevent
        ></PInput>
        <PBtn
          class="q-mb-lg"
          href="https://services.gst.gov.in/services/searchhsnsac"
          target="_blank"
          dense
          flat
          link
        >
          <SearchIcon class="icon--sm q-mr-xxs" />
          <span>Search HSN Code</span>
        </PBtn>
      </div>
      <div class="col-6">
        <PInput
          v-model.trim="formData.sku"
          label="SKU"
          placeholder="Enter SKU Code"
          @keydown.space.prevent
        />
      </div>
    </div>
  </PForm>
</template>

<script setup>
import productFormModel from "src/modules/product/models/productForm";
import SearchIcon from "assets/icons/search.svg";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import { useQuasar } from "quasar";
import { computed, unref } from "vue";

import { upperFirstFunc } from "assets/scripts/utilities/helpers";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";

import { useRequestAgain } from "../composables/requestAgain";
import { useApproveProduct } from "../composables/approve";
import { useRejectProduct } from "../composables/reject";

const { formState, product } = storeToRefs(useProductStore());
const { permissions } = storeToRefs(useAuthStore());

const { postProduct, putProduct, toggleProductForm, putProductStatus } =
  useProductStore();

const pForm = ref(null);

const $q = useQuasar();

const productHeading = computed(() => {
  if (!formState.value.edit) {
    if (!formState.value.edit) {
      return unref(permissions)?.products?.request ? "Request" : "Add";
    }
  }

  return formData.value.status_cd === "requested" ? "Review" : "View/Edit";
});

const { formData, isLoading, closeForm, resetForm, submitForm } = useForm({
  formModel: productFormModel(),
  entity: product,
  formRef: pForm,
  isForm: formState,
  post: postProduct,
  put: putProduct,
  toggleForm: toggleProductForm,
});

const { requestAgainButton } = useRequestAgain({
  formData,
  closeForm,
  permissions,
});

const { approveButton } = useApproveProduct({
  formData,
  closeForm,
  permissions,
});

const { rejectButton } = useRejectProduct({
  formData,
  closeForm,
  permissions,
});

const updateMarginExpectation = (subCategory) => {
  if (subCategory?.margin_expectation) {
    unref(formData).margin_expectation = subCategory?.margin_expectation;
  }
};

const brandFilter = useFilter({ entity: "brand" });
const categoryFilter = useFilter({
  entity: "category",
  options: { params: { isSubCategory: 0, isActive: 1 } },
});

const gstFilter = useFilter({
  entity: "gstSlabs",
});

let subCategoryFilter = {};

const initSubCategoryFilter = (category) => {
  unref(formData).subcategory_id = null;

  subCategoryFilter = useFilter({
    entity: "category",
    options: {
      params: {
        isSubCategory: 1,
        categoryId:
          unref(formData).category_id?._id ||
          unref(formData).category_id ||
          null,
      },
    },
  });

  updateMarginExpectation(category);
};

initSubCategoryFilter();

const { uoms } = storeToRefs(useUomStore());

const unitFilter = useFilter({
  entities: uoms,
  entity: "uom",
});

const isToggling = ref(false);

const toggleStatus = async (dialog) => {
  dialog.hide();

  isToggling.value = true;

  await putProductStatus(unref(formData));

  isToggling.value = false;

  closeForm();
};

const confirmToggleStatus = () => {
  const { is_active, name } = unref(formData);

  let title = "Activate";

  if (is_active) {
    title = "Deactivate";
  }

  const dialog = $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      title: `${title} Product`,
      text: `Are you sure you want to ${title} ${name}?`,
      isFooterVisible: true,
      submittable: true,
      cancelLabel: "Cancel",
      closable: true,
      onConfirm: () => toggleStatus(dialog),
    },
  });
};

const onReset = () => {
  resetForm();

  initSubCategoryFilter();
};

watch(
  () => unref(formData).gst_exempted,
  (gst_exempted) => {
    if (gst_exempted) {
      unref(formData).gst_rate = 0;

      unref(formData).cess = 0;
    } else {
      unref(formData).gst_rate = null;
    }
  },
);

const computedCess = computed({
  get: () => {
    return Number(unref(formData).cess);
  },
  set: (cess) => {
    unref(formData).cess = cess;
  },
});

const primaryCta = computed(() => {
  if (!formState.value.edit) {
    if (unref(permissions)?.products?.add) {
      return {
        loading: unref(isLoading),
      };
    } else if (unref(permissions)?.products?.request) {
      return {
        label: "Submit Request",
        type: "submit",
        loading: unref(isLoading),
        disable: false,
      };
    }

    return { loading: unref(isLoading) };
  }

  switch (unref(formData).status_cd) {
    case "rejected":
      return unref(requestAgainButton);

    case "requested":
      return unref(approveButton);

    case "approved":
      break;

    default:
      return { loading: unref(isLoading) };
  }

  return { loading: unref(isLoading) };
});

const secondaryCta = computed(() => {
  if (!formState.value.edit) {
    return unref(permissions)?.products?.add ? undefined : {};
  }

  const status = unref(formData).status_cd;

  if (status === "requested") {
    return unref(rejectButton);
  }

  if (status !== "rejected") {
    return undefined;
  }

  return {};
});

const ternaryCta = computed(() => {
  if (unref(formData).status_cd !== "approved") {
    return {};
  }

  const statusButton = {
    loading: unref(isToggling),
    outline: true,
    type: "button",
    disable: !unref(permissions)?.products?.edit,
    onClick: confirmToggleStatus,
  };

  if (unref(formData).is_active) {
    statusButton.label = "Deactivate";

    statusButton.color = "red";
  } else {
    statusButton.label = "Activate";

    statusButton.color = "primary";
  }

  return statusButton;
});
</script>
